import {Tooltip} from 'argo-ui';
import {Boundary, Placement} from 'popper.js';
import {useData} from 'argo-ui/v2';
import * as React from 'react';
import {Context} from '../shared/context';
import {services, ViewPreferences} from '../shared/services';

require('./sidebar.scss');

interface SidebarNavItem {
    path: string;
    iconClassName: string;
    title: string;
    tooltip?: string;
    group?: string;
}

interface SidebarProps {
    onVersionClick: () => void;
    navItems: SidebarNavItem[];
    pref: ViewPreferences;
}

export const SIDEBAR_TOOLS_ID = 'sidebar-tools';

export const useSidebarTarget = () => {
    const sidebarTarget = React.useRef(document.createElement('div'));

    React.useEffect(() => {
        const sidebar = document.getElementById(SIDEBAR_TOOLS_ID);
        sidebar.appendChild(sidebarTarget?.current);
        return () => {
            sidebarTarget.current?.remove();
        };
    }, []);

    return sidebarTarget;
};

const tooltipProps = {
    placement: 'right' as Placement,
    popperOptions: {
        modifiers: {
            preventOverflow: {
                boundariesElement: 'window' as Boundary
            }
        }
    }
};

const groupNavItems = (items: SidebarNavItem[]) =>
    items.reduce<{name: string; items: SidebarNavItem[]}[]>((groups, item) => {
        const name = item.group || '';
        const existing = groups.find(group => group.name === name);
        if (existing) {
            existing.items.push(item);
        } else {
            groups.push({name, items: [item]});
        }
        return groups;
    }, []);

const WithTooltip = (props: {show: boolean; content: string; children: React.ReactElement}) =>
    props.show ? (
        <Tooltip content={<div className='sidebar__tooltip'>{props.content}</div>} {...tooltipProps}>
            {props.children}
        </Tooltip>
    ) : (
        props.children
    );

export const Sidebar = (props: SidebarProps) => {
    const context = React.useContext(Context);
    const [version, loading, error] = useData(() => services.version.version());
    const locationPath = context.history.location.pathname;
    const collapsed = props.pref.hideSidebar;
    const toggleCollapsed = () => services.viewPreferences.updatePreferences({...props.pref, hideSidebar: !collapsed});
    const isActive = (path: string) => locationPath === path || locationPath.startsWith(`${path}/`);

    return (
        <nav className={`sidebar ${collapsed ? 'sidebar--collapsed' : ''}`} aria-label='Main navigation'>
            <div className='sidebar__brand'>
                <button type='button' className='sidebar__brand-link' onClick={() => context.history.push('/')} title='Go to start page'>
                    <img src='assets/sophistec/mark.png' alt='Sophistec' className='sidebar__brand-mark' />
                    {!collapsed && (
                        <span className='sidebar__brand-text'>
                            <span className='sidebar__brand-name'>Sophistec</span>
                            <span className='sidebar__brand-sub'>Deployments</span>
                        </span>
                    )}
                </button>
            </div>

            <div className='sidebar__container'>
                {groupNavItems(props.navItems || []).map(group => (
                    <div key={group.name || 'default'} className='sidebar__group'>
                        {group.name && (collapsed ? <div className='sidebar__group-divider' /> : <div className='sidebar__group-label'>{group.name}</div>)}
                        {group.items.map(item => (
                            <WithTooltip key={item.path} show={collapsed || !!item.tooltip} content={item.tooltip || item.title}>
                                <a
                                    href={item.path}
                                    className={`sidebar__nav-item ${isActive(item.path) ? 'sidebar__nav-item--active' : ''}`}
                                    aria-current={isActive(item.path) ? 'page' : undefined}
                                    aria-label={item.title}
                                    onClick={e => {
                                        e.preventDefault();
                                        context.history.push(item.path);
                                    }}>
                                    <i className={`sidebar__nav-icon ${item.iconClassName || ''}`} aria-hidden='true' />
                                    {!collapsed && <span className='sidebar__nav-label'>{item.title}</span>}
                                </a>
                            </WithTooltip>
                        ))}
                    </div>
                ))}

                {collapsed && (
                    <WithTooltip show={true} content='Show filters'>
                        <button type='button' onClick={toggleCollapsed} className='sidebar__nav-item sidebar__filter-button' aria-label='Show filters'>
                            <i className='sidebar__nav-icon fa fa-filter' aria-hidden='true' />
                        </button>
                    </WithTooltip>
                )}

                <div id={SIDEBAR_TOOLS_ID} className='sidebar__tools' />
            </div>

            <div className='sidebar__footer'>
                {!collapsed && (
                    <button type='button' className='sidebar__version' onClick={props.onVersionClick} title='Server version'>
                        {loading ? 'Loading…' : error?.state ? 'Unknown' : version?.Version || 'Unknown'}
                    </button>
                )}
                <button type='button' onClick={toggleCollapsed} className='sidebar__collapse-button' aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
                    <i className={`fa fa-angles-${collapsed ? 'right' : 'left'}`} aria-hidden='true' />
                </button>
            </div>
        </nav>
    );
};
